import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import { UserType } from '@/models/user'

import { routeNames } from './route-names'
import { layoutNames } from '@/layouts/layout-names'

import {
  castBooleans,
  castPagination,
  castStrings,
  castStringsArray,
} from '@/helpers/query'
import { getScope, RbacAction, RbacEntity } from '@/helpers/rbac'

import authMiddleware from '@/middlewares/auth'
import rbacGuard from '@/middlewares/rbac'

import Dashboard from '@/views/Dashboard.vue'
import NotFound from '@/views/NotFound.vue'

const { READ } = RbacAction

const routes: Array<RouteRecordRaw> = [
  {
    path: '/localization',
    name: routeNames.localization,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.APP_SETTINGS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "localization" */ '../views/Localization/LocalizationList.vue'
      ),
  },
  {
    path: '/golf-localization',
    name: routeNames.golfLocalization,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.APP_SETTINGS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-localization" */ '../views/GolfLocalization/index.vue'
      ),
  },
  {
    path: '/',
    name: routeNames.dashboard,
    meta: { layout: layoutNames.main },
    component: Dashboard,
  },
  {
    path: '/login',
    name: routeNames.login,
    meta: {
      layout: layoutNames.empty,
    },
    component: () =>
      import(/* webpackChunkName: "about" */ '../views/Login.vue'),
  },
  {
    path: '/about',
    name: routeNames.about,
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "about" */ '../views/About.vue'),
  },
  {
    path: '/users',
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
        ...castStrings(query, [
          'status',
          'isCommercial',
          'search_admin_poa',
          'is_admin_poa',
          'q',
          'firstName',
          'lastName',
          'expired_driver_license',
          'next_month_birthday',
          'search_end_date_poa',
          'expiring_driver_license_soon',
        ]),
        ...castBooleans(query, ['misconduct', 'isBalance']),
      },
    }),
    name: routeNames.users,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.USERS)],
    },
    component: () =>
      import(/* webpackChunkName: "users" */ '../views/Users/List.vue'),
  },
  {
    path: '/users/:id',
    name: routeNames.userDetails,
    props: true,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.USERS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "users" */ '../views/Users/Details/index.vue'
      ),
  },
  {
    path: '/vehicles',
    name: routeNames.vehicles,
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
        ...castStrings(query, ['status', 'user_id']),
      },
    }),
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.CARS)],
    },
    component: () =>
      import(/* webpackChunkName: "vehicles" */ '../views/Vehicles/List.vue'),
  },
  {
    path: '/vehicles/:id',
    name: routeNames.vehicleDetails,
    props: true,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.CARS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "vehicles" */ '../views/Vehicles/Details/index.vue'
      ),
  },
  {
    path: '/add-vehicle',
    name: routeNames.addVehicle,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(RbacAction.CREATE, RbacEntity.CARS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "vehicles" */ '../views/Vehicles/AddVehicle.vue'
      ),
  },
  {
    path: '/trips',
    name: routeNames.trips,
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
        ...castBooleans(query, [
          'cancelled',
          'damaged',
          'checkIn',
          'checkOut',
          'isFinalize',
        ]),
        ...castStrings(query, [
          'car_profile_id',
          'host_id',
          'renter_id',
          'insurance_scripts',
        ]),
        failedPayments: Boolean(query.failedPayments) || undefined,
        statuses: castStringsArray(query.statuses),
      },
    }),
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.TRIPS)],
    },

    component: () =>
      import(/* webpackChunkName: "trips" */ '../views/Trips/List.vue'),
  },
  {
    path: '/trips/:id',
    name: routeNames.tripDetails,
    props: true,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.TRIPS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "trips" */ '../views/Trips/Details/index.vue'
      ),
  },
  {
    path: '/settings',
    name: routeNames.settings,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.APP_SETTINGS)],
    },
    component: () =>
      import(/* webpackChunkName: "settings" */ '../views/Settings.vue'),
  },
  {
    path: '/notifications',
    name: routeNames.notifications,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.APP_SETTINGS)],
    },
    component: () =>
      import(/* webpackChunkName: "settings" */ '../views/Notifications.vue'),
  },
  {
    path: '/car-data',
    name: routeNames.carData,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.APP_SETTINGS)],
    },
    component: () =>
      import(/* webpackChunkName: "settings" */ '../views/CarData.vue'),
  },
  {
    path: '/security-groups',
    name: routeNames.securityGroups,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.ADMIN_PROFILES)],
    },
    component: () =>
      import(
        /* webpackChunkName: "security groups" */ '../views/SecurityGroups.vue'
      ),
  },
  {
    path: '/reviews',
    name: routeNames.reviews,
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
        userType:
          query.userType === UserType.HOST ? UserType.HOST : UserType.RENTER,
      },
    }),
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.USERS)],
    },
    component: () =>
      import(/* webpackChunkName: "reviews" */ '../views/Reviews/List.vue'),
  },
  {
    path: '/reviews/:id',
    props: true,
    name: routeNames.reviewDetails,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.USERS)],
    },
    component: () =>
      import(/* webpackChunkName: "reviews" */ '../views/Reviews/Details.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: routeNames.notFound,
    meta: { layout: layoutNames.empty },
    component: NotFound,
  },
  {
    path: '/cars/create-ev',
    name: routeNames.createEvCar,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(RbacAction.CREATE, RbacEntity.CARS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "create-ev-car" */ '../views/EVCars/CreateEvCar.vue'
      ),
  },
  {
    path: '/ev-vehicles',
    name: routeNames.evVehicles,
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
        ...castStrings(query, ['status', 'external_id']),
        ...castBooleans(query, ['is_available']),
      },
    }),
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-vehicles-list" */ '../views/EVCars/List.vue'
      ),
  },
  {
    path: '/ev-vehicles/:id',
    name: routeNames.evVehicleDetails,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-vehicle-details" */ '../views/EVCars/Details/index.vue'
      ),
    props: true,
  },

  {
    path: '/ev-trips',
    name: routeNames.evTrips,
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
        statuses: castStringsArray(query.statuses),
      },
    }),
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-vehicles-list" */ '../views/EVTrips/List.vue'
      ),
  },
  {
    path: '/ev-trips/:id',
    name: routeNames.evTripDetails,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-vehicle-details" */ '../views/EVTrips/Details/index.vue'
      ),
    props: true,
  },
  {
    path: '/ev-users',
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
        ...castStrings(query, [
          'status',
          'status_ev',
          'q',
          'firstName',
          'lastName',
        ]),
      },
    }),
    name: routeNames.evUsers,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.USERS)],
    },
    component: () =>
      import(/* webpackChunkName: "ev-users" */ '../views/EVUsers/List.vue'),
  },
  {
    path: '/ev-users/:id',
    name: routeNames.evUserDetails,
    props: true,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.USERS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-users" */ '../views/EVUsers/Details/index.vue'
      ),
  },
  // EV Module Map page
  {
    path: '/ev-map',
    name: routeNames.evMap,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.CARS)],
    },
    component: () =>
      import(/* webpackChunkName: "ev-map" */ '../views/EVMap.vue'),
  },
  {
    path: '/ev-map-areas',
    name: routeNames.evMapAreas,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.CARS)],
    },
    component: () =>
      import(/* webpackChunkName: "ev-map-areas" */ '../views/EVMapAreas.vue'),
  },
  {
    path: '/ev-map-areas/alert',
    name: routeNames.evMapAreasAlert,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.CARS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-map-areas-alert" */ '../views/EVMapAreasAlert.vue'
      ),
  },
  {
    path: '/ev-balance',
    name: routeNames.evBalance,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.USERS)],
    },
    component: () => ({
      template: `<EVPlaceholder 
        title="EV Balance" 
        description="Financial balance overview and transaction management for EV operations." 
      />`,
      components: {
        EVPlaceholder: () => import('../views/EVPlaceholder.vue'),
      },
    }),
  },
  {
    path: '/ev-reviews',
    name: routeNames.evReviews,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.USERS)],
    },
    component: () => ({
      template: `<EVPlaceholder 
        title="EV Reviews" 
        description="Review management system for EV module trips and user experiences." 
      />`,
      components: {
        EVPlaceholder: () => import('../views/EVPlaceholder.vue'),
      },
    }),
  },
  {
    path: '/ev-reports',
    name: routeNames.evReports,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.TRIPS)],
    },
    component: () =>
      import(/* webpackChunkName: "ev-reports" */ '../views/EVReports.vue'),
  },
  {
    path: '/ev-discrepencies',
    name: routeNames.evDiscrepencies,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.TRIPS)],
    },
    component: () => ({
      template: `<EVPlaceholder 
        title="EV Discrepancies" 
        description="Track and manage discrepancies in EV operations, maintenance, and billing." 
      />`,
      components: {
        EVPlaceholder: () => import('../views/EVPlaceholder.vue'),
      },
    }),
  },
  {
    path: '/ev-reserves',
    name: routeNames.evReserves,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.TRIPS)],
    },
    component: () =>
      import(/* webpackChunkName: "ev-reserves" */ '../views/EVReserves.vue'),
  },
  {
    path: '/ev-outstanding',
    name: routeNames.evOutstanding,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.TRIPS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-outstanding" */ '../views/EVOutstanding.vue'
      ),
  },
  {
    path: '/ev-statistic',
    name: routeNames.evStatistic,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.CARS)],
    },
    component: () =>
      import(/* webpackChunkName: "ev-statistic" */ '../views/EVStatistic.vue'),
  },
  {
    path: '/ev-trip-points',
    name: routeNames.evTripPoints,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.CARS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-trip-points" */ '../views/EVTripPoints.vue'
      ),
  },
  {
    path: '/ev-settings',
    name: routeNames.evSettings,
    meta: {
      layout: layoutNames.main,
      scopes: [getScope(READ, RbacEntity.CARS)],
    },
    component: () =>
      import(/* webpackChunkName: "ev-settings" */ '../views/EVSettings.vue'),
  },
  {
    path: '/ev-owner-statistics',
    name: routeNames.evOwnerStatistics,
    meta: {
      layout: layoutNames.main,
      // scopes: [getScope(READ, RbacEntity.EV_OWNER_STATISTICS)],
    },
    component: () =>
      import(
        /* webpackChunkName: "ev-owner-statistics" */ '../views/EVOwnerStatistics.vue'
      ),
  },
  {
    path: '/golf-cars',
    name: routeNames.golfCars,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-cars-list" */ '../views/GolfCars/List.vue'
      ),
  },
  {
    path: '/golf-cars/create-make',
    name: routeNames.golfCarCreateMake,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-car-create-make" */ '../views/GolfCars/CreateMake.vue'
      ),
  },
  {
    path: '/golf-cars/create-model',
    name: routeNames.golfCarCreateModel,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-car-create-model" */ '../views/GolfCars/CreateModel.vue'
      ),
  },
  {
    path: '/golf-cars/:id',
    name: routeNames.golfCarDetails,
    props: true,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-car-details" */ '../views/GolfCars/Details/index.vue'
      ),
  },
  {
    path: '/golf-trips',
    name: routeNames.golfTrips,
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
        statuses: castStringsArray(query.statuses),
      },
    }),
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-trips-list" */ '../views/GolfTrips/List.vue'
      ),
  },
  {
    path: '/golf-trips/:id',
    name: routeNames.golfTripDetails,
    props: true,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-trip-details" */ '../views/GolfTrips/Details/index.vue'
      ),
  },
  {
    path: '/golf-users',
    name: routeNames.golfUsers,
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
      },
    }),
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-users-list" */ '../views/GolfUsers/List.vue'
      ),
  },
  {
    path: '/golf-users/:id',
    name: routeNames.golfUserDetails,
    props: true,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-user-details" */ '../views/GolfUsers/Details/index.vue'
      ),
  },
  {
    path: '/golf-settings',
    name: routeNames.golfSettings,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-settings" */ '../views/GolfSettings/index.vue'
      ),
  },
  {
    path: '/golf-tariff-settings',
    name: routeNames.golfTariffSettings,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-tariff-settings" */ '../views/GolfTariffSettings/index.vue'
      ),
  },
  {
    path: '/golf-admin-roles',
    name: routeNames.golfAdminRoles,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-admin-roles" */ '../views/GolfAdminRoles/index.vue'
      ),
  },
  {
    path: '/golf-reserves',
    name: routeNames.golfReserves,
    props: ({ query }: { query: Record<string, any> }) => ({
      query: {
        ...castPagination(query),
      },
    }),
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-reserves-list" */ '../views/GolfReserves/List.vue'
      ),
  },
  {
    path: '/golf-map',
    name: routeNames.golfMap,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(/* webpackChunkName: "golf-map" */ '../views/GolfMap.vue'),
  },
  {
    path: '/golf-map-areas',
    name: routeNames.golfMapAreas,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-map-areas" */ '../views/GolfMapAreas.vue'
      ),
  },
  {
    path: '/golf-map-areas/alert',
    name: routeNames.golfMapAreasAlert,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-map-areas-alert" */ '../views/GolfMapAreasAlert.vue'
      ),
  },
  {
    path: '/golf-dashboard',
    name: routeNames.golfDashboard,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-dashboard" */ '../views/GolfDashboard/index.vue'
      ),
  },
  {
    path: '/golf-financial-report',
    name: routeNames.golfFinancialReport,
    meta: {
      layout: layoutNames.main,
    },
    component: () =>
      import(
        /* webpackChunkName: "golf-financial-report" */ '../views/GolfFinancialReport/index.vue'
      ),
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

router.beforeEach(authMiddleware)
router.beforeEach(rbacGuard)

export default router
